import React, { useRef, useEffect, FC, memo } from 'react';
import { createPortal } from 'react-dom';
import { useForceUpdate, OnClickOutsideProvider } from '@pushpay/utils';
import { Footer as CapFooter, Navigation as CapNavigation } from '@pushpay/cap-layout';
import { NavigationItem as CapNavigationItem } from '@pushpay/cap-layout/types';

import { LoggedInWebThemeProvider } from '../components/theming';
import { Fragment } from '../../Shared/components/fragment';
import { NavViewModel, NavItem, NavMenuItemType } from '../loggedinweb-generated';
import { Header } from './cap-header';
import { LinkAndSync } from './link-and-sync';
import { useMyOrganizations } from './my-organizations';

export const CapLayout: FC<{ vm: NavViewModel }> = memo(({ vm }) => {
	const headerContainer = useRef<HTMLDivElement | null>(null);
	const navigationContainer = useRef<HTMLDivElement | null>(null);
	const footerContainer = useRef<HTMLDivElement | null>(null);
	const forceUpdate = useForceUpdate();

	useEffect(() => {
		headerContainer.current = document.querySelector('#cap-header');
		navigationContainer.current = document.querySelector('#cap-navigation');
		footerContainer.current = document.querySelector('#cap-footer');
		forceUpdate();
	}, []);

	if (!vm) {
		console.log('CapLayout component loaded but will exit because vm is not ready.');
		return null;
	}

	const {
		NavInfo: navInfo,
		CurrentActiveOrganization: currentOrganization,
		AdministeredOrganizationInfo: { Items: organizationItems },
	} = vm;

	const {
		Header: { DisplayName: headerDisplayName, Url: headerUrl, LogoUrl: headerLogoUrl },
		Items: navItems,
		Footer: { Title: footerTitle, Text: footerText, Url: footerUrl },
	} = navInfo;

	const mapToNavigationItem = (item: NavItem): CapNavigationItem => {
		const navItem = {
			key: item.KeyAsString,
			displayName: item.DisplayName,
			url: item.Url,
			routePath: item.RoutePath,
			warning:
				item.Key === NavMenuItemType.ApiSettingsSubMenu && item.WarningUrl
					? { warningText: item.WarningText, warningUrl: item.WarningUrl }
					: null,
			items: item.Items && item.Items.length ? item.Items.map((i) => mapToNavigationItem(i)) : null,
			render: undefined,
		};

		if (
			item.Key === NavMenuItemType.Community &&
			currentOrganization.IsCcbActive &&
			!currentOrganization.CcbPeopleLinkingEnabled
		) {
			navItem.render = (
				<LinkAndSync
					displayName={item.DisplayName}
					data-pp-at-target="nav-link-and-sync-disabled"
					linkAndSyncEnabled={false}
				/>
			);
		} else if (item.Key === NavMenuItemType.Community && currentOrganization.CcbPeopleLinkingEnabled) {
			navItem.render = (
				<LinkAndSync
					displayName={item.DisplayName}
					data-pp-at-target="nav-link-and-sync-enabled"
					linkAndSyncEnabled
				/>
			);
		}

		return navItem;
	};

	const items = navItems.map<CapNavigationItem>(mapToNavigationItem);

	const footerYear = new Date().getFullYear();

	const myOrganizations = useMyOrganizations(organizationItems, currentOrganization);

	return (
		<OnClickOutsideProvider>
			<LoggedInWebThemeProvider>
				<Fragment>
					{headerContainer.current &&
						createPortal(<Header vm={vm} {...myOrganizations} />, headerContainer.current)}
					{navigationContainer.current &&
						createPortal(
							<CapNavigation
								deprecated={!NewFeatures.UseNewNavigationSideMenu}
								currentOrganization={myOrganizations.currentOrganization}
								organizations={myOrganizations.organizations}
								selectOrganizationLabel="Select Organization"
								mobileButtonLabel="Open main navigation"
								data-pp-at-target="Navigation"
								data={{
									header: {
										displayName: headerDisplayName,
										url: headerUrl,
										logoUrl: headerLogoUrl,
									},
									items,
									footer: {
										title: footerTitle,
										text: footerText,
										url: footerUrl,
									},
								}}
								isSPA={false}
							/>,
							navigationContainer.current
						)}
					{footerContainer.current &&
						createPortal(
							<CapFooter data-pp-at-target="Footer" year={footerYear} />,
							footerContainer.current
						)}
				</Fragment>
			</LoggedInWebThemeProvider>
		</OnClickOutsideProvider>
	);
});
